// Tisková fronta — všechny vygenerované dopisy seskupené podle statusu.
//
// Workflow:
//   1. Filter na status (default „queued").
//   2. Multi-select řádků zaškrtávátkem.
//   3. Toolbar nahoře (jen když máš výběr):
//        - Stáhnout dávku jako jedno PDF
//        - Označit jako vytištěno
//        - Označit jako odesláno
//   4. Per-row PDF link + manuální status change.
//
// Dávkové stažení posílá ids serveru, dostane zpět merged PDF (vyvolá download
// v prohlížeči). Status NEměníme automaticky — uživatel po fyzickém tisku
// klikne „Označit jako vytištěno".

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { MailingListItem, MailingStatus } from '@/lib/types';
import { cn } from '@/lib/utils';

const STATUS_FILTERS: { key: MailingStatus | 'all'; label: string }[] = [
  { key: 'queued', label: 'Ve frontě' },
  { key: 'printed', label: 'Vytištěné' },
  { key: 'posted', label: 'Odeslané' },
  { key: 'returned', label: 'Vrácené' },
  { key: 'all', label: 'Vše' },
];

export default function PrintQueue() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<MailingStatus | 'all'>('queued');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const query = useQuery({
    queryKey: ['mailings', 'queue'],
    queryFn: () => api<{ ok: true; items: MailingListItem[]; count: number }>('/api/mailings'),
  });

  const items = query.data?.items || [];

  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter((x) => x.mailing.status === filter)),
    [items, filter],
  );

  // Counts pro chips — zobrazují celkové číslo na status (nezávisle na současném filteru).
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: items.length };
    for (const x of items) c[x.mailing.status] = (c[x.mailing.status] || 0) + 1;
    return c;
  }, [items]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((x) => selected.has(x.mailing.id));
  const someFilteredSelected = filtered.some((x) => selected.has(x.mailing.id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (allFilteredSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((x) => next.delete(x.mailing.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((x) => next.add(x.mailing.id));
        return next;
      });
    }
  }

  const selectedIds = useMemo(
    () => filtered.filter((x) => selected.has(x.mailing.id)).map((x) => x.mailing.id),
    [filtered, selected],
  );

  /* ----- Mutations ----- */

  const downloadBatch = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await fetch('/api/mailings/batch-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new ApiError(res.status, 'Stažení selhalo');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dopisy-${new Date().toISOString().slice(0, 10)}-${ids.length}ks.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });

  const setStatus = useMutation({
    mutationFn: (input: { ids: string[]; status: MailingStatus }) =>
      api('/api/mailings/batch-status', { method: 'POST', json: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mailings'] });
      setSelected(new Set());
    },
  });

  const setOneStatus = useMutation({
    mutationFn: (input: { id: string; status: MailingStatus }) =>
      api(`/api/mailings/${input.id}`, { method: 'PATCH', json: { status: input.status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mailings'] }),
  });

  /* ----- Render ----- */

  return (
    <div className="space-y-4 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Tisková fronta</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Vyber dopisy → stáhni jedno spojené PDF → vytiskni → označ vytištěné / odeslané.
        </p>
      </header>

      <div className="flex flex-wrap gap-1 border-b">
        {STATUS_FILTERS.map((f) => {
          const n = counts[f.key] ?? 0;
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                'px-3 py-2 -mb-px border-b-2 text-sm transition-colors',
                active
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {f.label}
              <span className="ml-1.5 text-xs text-muted-foreground">{n}</span>
            </button>
          );
        })}
      </div>

      {/* Toolbar — sticky když je výběr */}
      {selectedIds.length > 0 && (
        <div className="sticky top-2 z-10 rounded-lg border bg-background shadow-sm p-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium">Vybráno {selectedIds.length}</span>
          <div className="flex flex-wrap gap-2 ml-auto">
            <button
              onClick={() => downloadBatch.mutate(selectedIds)}
              disabled={downloadBatch.isPending}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
            >
              {downloadBatch.isPending ? 'Generuji…' : `↓ Stáhnout dávku PDF (${selectedIds.length})`}
            </button>
            <button
              onClick={() => setStatus.mutate({ ids: selectedIds, status: 'printed' })}
              disabled={setStatus.isPending}
              className="px-3 py-1.5 rounded-md border text-sm hover:bg-muted disabled:opacity-50"
            >
              Označit jako vytištěné
            </button>
            <button
              onClick={() => setStatus.mutate({ ids: selectedIds, status: 'posted' })}
              disabled={setStatus.isPending}
              className="px-3 py-1.5 rounded-md border text-sm hover:bg-muted disabled:opacity-50"
            >
              Označit jako odeslané
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:bg-muted"
            >
              Zrušit výběr
            </button>
          </div>
          {downloadBatch.error && (
            <span className="basis-full text-xs text-destructive">
              Chyba: {String(downloadBatch.error)}
            </span>
          )}
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 w-10">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected;
                  }}
                  onChange={toggleAll}
                  disabled={filtered.length === 0}
                />
              </th>
              <th className="text-left px-3 py-2 font-medium w-28">Status</th>
              <th className="text-left px-3 py-2 font-medium">Adresát</th>
              <th className="text-left px-3 py-2 font-medium">Šablona</th>
              <th className="text-left px-3 py-2 font-medium">Nemovitost</th>
              <th className="text-right px-3 py-2 font-medium">Vytvořeno</th>
              <th className="text-right px-3 py-2 font-medium w-32">Akce</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {query.isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  Načítám…
                </td>
              </tr>
            )}
            {!query.isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  {items.length === 0
                    ? 'Žádné dopisy zatím nebyly vygenerované.'
                    : `Žádné dopisy ve stavu „${STATUS_FILTERS.find((f) => f.key === filter)?.label.toLowerCase()}".`}
                </td>
              </tr>
            )}
            {filtered.map((x) => (
              <Row
                key={x.mailing.id}
                item={x}
                checked={selected.has(x.mailing.id)}
                onCheck={() => toggle(x.mailing.id)}
                onSetStatus={(status) => setOneStatus.mutate({ id: x.mailing.id, status })}
                statusPending={setOneStatus.isPending && setOneStatus.variables?.id === x.mailing.id}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({
  item,
  checked,
  onCheck,
  onSetStatus,
  statusPending,
}: {
  item: MailingListItem;
  checked: boolean;
  onCheck: () => void;
  onSetStatus: (s: MailingStatus) => void;
  statusPending: boolean;
}) {
  const { mailing, template, owner } = item;
  const nextStatus = nextStatusFor(mailing.status as MailingStatus);

  return (
    <tr className={cn('hover:bg-muted/30', checked && 'bg-primary/5')}>
      <td className="px-3 py-2.5">
        <input type="checkbox" checked={checked} onChange={onCheck} />
      </td>
      <td className="px-3 py-2.5">
        <StatusBadge status={mailing.status as MailingStatus} />
      </td>
      <td className="px-3 py-2.5">{owner.name}</td>
      <td className="px-3 py-2.5 text-xs">{template.name}</td>
      <td className="px-3 py-2.5">
        <Link
          to={`/properties/${mailing.propertyId}`}
          className="text-xs text-muted-foreground hover:underline"
        >
          {mailing.propertyId.slice(0, 8)}…
        </Link>
      </td>
      <td className="px-3 py-2.5 text-right text-xs text-muted-foreground tabular-nums">
        {new Date(mailing.queuedAt).toLocaleDateString('cs-CZ')}
      </td>
      <td className="px-3 py-2.5 text-right">
        <div className="flex justify-end gap-1.5">
          {mailing.pdfFileId && (
            <a
              href={`/api/mailings/${mailing.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="px-2 py-1 rounded border text-[11px] hover:bg-muted"
            >
              PDF
            </a>
          )}
          {nextStatus && (
            <button
              onClick={() => onSetStatus(nextStatus)}
              disabled={statusPending}
              title={`Posunout na: ${STATUS_LABELS[nextStatus]}`}
              className="px-2 py-1 rounded border border-primary/40 text-[11px] hover:bg-primary/10 disabled:opacity-50"
            >
              → {STATUS_LABELS[nextStatus]}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

const STATUS_LABELS: Record<MailingStatus, string> = {
  queued: 've frontě',
  printed: 'vytištěno',
  posted: 'odesláno',
  returned: 'vráceno',
  acknowledged: 'reakce',
};

function nextStatusFor(s: MailingStatus): MailingStatus | null {
  if (s === 'queued') return 'printed';
  if (s === 'printed') return 'posted';
  return null;
}

function StatusBadge({ status }: { status: MailingStatus }) {
  const palette: Record<MailingStatus, string> = {
    queued: 'bg-amber-100 text-amber-900 border-amber-200',
    printed: 'bg-blue-100 text-blue-900 border-blue-200',
    posted: 'bg-emerald-100 text-emerald-900 border-emerald-200',
    returned: 'bg-destructive/10 text-destructive border-destructive/40',
    acknowledged: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span
      className={cn(
        'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border',
        palette[status],
      )}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}
